/*
 * Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { globalRef } from '@amazon/vinyl-util'

export type Fetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>

interface ResponseHead {
    readonly status: number
    readonly statusText: string
    readonly url: string
    readonly headers: [string, string][]
}

type BodyMessage = ArrayBuffer | { readonly end: true } | { readonly error: string }

export interface ShellChannel {
    onmessage: (message: BodyMessage) => void
}

/** The slice of `@tauri-apps/api/core` the shell's fetch uses. */
export interface ShellBridge {
    invoke<T>(command: string, args: Record<string, unknown>): Promise<T>
    readonly Channel: new () => ShellChannel
}

const NULL_BODY_STATUSES = [101, 103, 204, 205, 304]

const requestIds = globalRef(() => ({ next: Math.floor(Math.random() * 2 ** 31) }))

/** Whether this shell has `native_fetch`; an older native build has only the HTTP plugin. */
export async function shellHasNativeFetch(bridge: ShellBridge): Promise<boolean> {
    return bridge.invoke('native_fetch_abort', { id: 0 }).then(
        () => true,
        () => false
    )
}

function abortError(signal?: AbortSignal | null): unknown {
    return signal?.reason ?? new DOMException('The operation was aborted.', 'AbortError')
}

async function requestBody(request: Request, init?: RequestInit) {
    if (typeof init?.body === 'string') return { text: init.body }
    if (!request.body) return null
    const bytes = new Uint8Array(await request.arrayBuffer())
    let binary = ''
    for (let i = 0; i < bytes.length; i += 0x8000) {
        binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
    }
    return { base64: btoa(binary) }
}

/** `fetch` through the shell's pooled client (`native_fetch.rs`); the body streams as it arrives. */
export function createShellFetch(bridge: ShellBridge): Fetch {
    return async (input, init) => {
        const request = new Request(input, init)
        const { signal } = request
        if (signal.aborted) throw abortError(signal)
        const body = await requestBody(request, init)
        const id = requestIds.value.next++
        // A request the shell already finished has nothing left to cancel.
        const cancel = () => {
            bridge.invoke<void>('native_fetch_abort', { id }).catch(() => {})
        }
        let open = true
        let controller!: ReadableStreamDefaultController<Uint8Array>
        const finish = (end: (c: typeof controller) => void) => {
            if (!open) return
            open = false
            signal.removeEventListener('abort', onAbort)
            end(controller)
        }
        const stream = new ReadableStream<Uint8Array>({
            start: (c) => {
                controller = c
            },
            cancel: () => {
                open = false
                signal.removeEventListener('abort', onAbort)
                cancel()
            },
        })
        const channel = new bridge.Channel()
        channel.onmessage = (message) => {
            if (!open) return
            if (message instanceof ArrayBuffer) controller.enqueue(new Uint8Array(message))
            else if ('end' in message) finish((c) => c.close())
            else finish((c) => c.error(new TypeError(message.error)))
        }
        let rejectHead: (reason: unknown) => void = () => {}
        function onAbort() {
            rejectHead(abortError(signal))
            finish((c) => c.error(abortError(signal)))
            cancel()
        }
        signal.addEventListener('abort', onAbort)
        const head = await new Promise<ResponseHead>((resolve, reject) => {
            rejectHead = reject
            bridge
                .invoke<ResponseHead>('native_fetch', {
                    request: {
                        id,
                        method: request.method,
                        url: request.url,
                        headers: [...request.headers],
                        body,
                    },
                    body: channel,
                })
                .then(resolve, (error: unknown) => {
                    finish(() => {})
                    reject(new TypeError(String(error)))
                })
        })
        const response = new Response(NULL_BODY_STATUSES.includes(head.status) ? null : stream, {
            status: head.status,
            statusText: head.statusText,
            headers: head.headers,
        })
        Object.defineProperty(response, 'url', { value: head.url })
        return response
    }
}

/**
 * Lets at most `max` requests wait for their response at once; the rest queue, a
 * `background` one behind every other.
 */
export function limitConcurrency(
    fetch: Fetch,
    max: number,
    background: (input: RequestInfo | URL) => boolean
): Fetch {
    let running = 0
    const waiting: { readonly start: () => void; readonly background: boolean }[] = []
    const next = () => {
        running--
        waiting.shift()?.start()
    }
    return (input, init) => {
        const run = () => {
            running++
            const response = fetch(input, init)
            response.then(next, next)
            return response
        }
        if (running < max) return run()
        const signal = init?.signal
        if (signal?.aborted) return Promise.reject(abortError(signal))
        return new Promise<Response>((resolve, reject) => {
            const entry = {
                background: background(input),
                start: () => {
                    signal?.removeEventListener('abort', drop)
                    run().then(resolve, reject)
                },
            }
            function drop() {
                waiting.splice(waiting.indexOf(entry), 1)
                reject(abortError(signal))
            }
            signal?.addEventListener('abort', drop, { once: true })
            const firstBackground = waiting.findIndex((w) => w.background)
            if (entry.background || firstBackground < 0) waiting.push(entry)
            else waiting.splice(firstBackground, 0, entry)
        })
    }
}
